import React, { Suspense, useEffect, useState } from "react"
import { Provider, useDispatch } from "react-redux"
import { BrowserRouter, Switch, Route, Redirect } from "react-router-dom"
import { HelmetProvider } from "react-helmet-async"
import { load as loadAuth } from "store/modules/common/auth"
import { loadLocaleFromBackEnd } from "store/modules/common/utils"
import i18next from "i18n"
import GlobalSpinner from "components/Spinner/GlobalSpinner"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import IncompleteOfferConfirmationPage from "routes/offers/confirm/[token]"
import { defaultLanguage } from "utils/api"
import * as WS from "shared/lib/web-sockets"
import { useWebSocketsCleanup } from "shared/hooks"
import { Notifications } from "shared/ui/berry-jass-extended"

import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import ReactQuerySeleniumTools from "utils/ReactQuerySeleniumTools"
import { Loadable } from "shared/ui/berry-jass"
import {
  isApplyRecrumediaDomain,
  isOffersRecrumediaDomain,
  isWhiteLabelLoginDomain
} from "shared"
import PrivateRoute from "components/Router/PrivateRoute"
import {
  useReminderNotifications,
  useShowActivityReminderNotification,
  useSubscribeToActivitiesReminders
} from "entities/activities-reminder"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false }
  }
})

const helmetContext = {}

const ApplyRecrumedia = React.lazy(() => import("routes/apply-recrumedia"))
const NotFound = React.lazy(() => import("routes/not-found"))
const App = React.lazy(() => import("components/App/App"))
const JassBerryApp = React.lazy(() => import("components/App/JassBerryApp"))
const WhiteLogin = Loadable(React.lazy(() => import("routes/white-login")))
const Login = Loadable(React.lazy(() => import("routes/login")))
const TwoFactorAuthRequiredSetup = Loadable(
  React.lazy(() => import("routes/two-factor-auth-required-setup"))
)
const Forgot = Loadable(
  React.lazy(() => import("routes/white-login/forgot-password"))
)
const ChangePassword = Loadable(
  React.lazy(() => import("routes/white-login/change-password"))
)
const OfferAccept = Loadable(React.lazy(() => import("routes/offers/accept")))
const OfferDraftsAccept = Loadable(
  React.lazy(() => import("routes/offer-drafts/accept"))
)

const Routes = () => {
  const dispatch = useDispatch()
  const [ready, setReady] = useState(false)
  const { refetch: getNotifications } = useReminderNotifications()
  const showActivityReminderNotification = useShowActivityReminderNotification()

  useSubscribeToActivitiesReminders()

  useEffect(() => {
    if (isApplyRecrumediaDomain() || isOffersRecrumediaDomain()) {
      setReady(true)
    } else {
      Promise.all([
        dispatch(loadAuth()).then((response) => {
          const locale = response?.user?.locale ?? defaultLanguage()
          i18next.changeLanguage(locale)

          if (response.user?.id) {
            WS.setup(response.user)

            if (response.user.unseen_todo_reminders_count > 0) {
              getNotifications().then(({ data: { data: notifications } }) =>
                notifications.forEach((notification) =>
                  showActivityReminderNotification(notification)
                )
              )
            }
          }
        }),
        dispatch(loadLocaleFromBackEnd()).then(({ translations, locale }) => {
          Object.entries(translations).forEach(([lng, keys]) => {
            i18next.addResourceBundle(lng, "translation", keys, true, true)
          })
          if (locale) i18next.changeLanguage(locale)
        })
      ]).then(() => setReady(true))
    }
  }, [dispatch, getNotifications, showActivityReminderNotification])

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.has("white-label-login")) {
      try {
        localStorage.setItem("white-label-login", "true")
      } catch {
        // eslint-disable-next-line no-console
        console.error("localStorage is not available")
      }
    }
  }, [])

  if (!ready) {
    return <GlobalSpinner />
  }

  return (
    <Suspense fallback={<GlobalSpinner />}>
      {isApplyRecrumediaDomain() && (
        <Switch>
          <Route path="/:vacancyToken">
            <ApplyRecrumedia />
          </Route>
          <Route path="*" component={NotFound} />
        </Switch>
      )}
      {isOffersRecrumediaDomain() && (
        <Switch>
          <Route path="/confirm/:token">
            <IncompleteOfferConfirmationPage />
          </Route>
          <Route path="*" component={NotFound} status={404} />
        </Switch>
      )}
      {!isApplyRecrumediaDomain() && !isOffersRecrumediaDomain() && (
        <Switch>
          <Route
            exact
            path="/"
            render={() => (
              <Redirect
                to={isWhiteLabelLoginDomain() ? "/r/login" : "/react/login"}
              />
            )}
          />

          <Route path="/react/offers/confirm/:token">
            <IncompleteOfferConfirmationPage />
          </Route>

          <Route path="/react/apply/:vacancyToken">
            <ApplyRecrumedia />
          </Route>

          <Route path="/react/login">
            <Login />
            <Notifications />
          </Route>
          <Route path="/react">
            <App />
          </Route>

          {/*

            1. Don't forget to update services_configs/nginx/nginx.autodeploy.conf and
            charts/white-label-login/templates/ingress.yaml in white-label-login repository.
            You’ll also need to edit the running ingress in the k8s cluster (for example, with k9s).

            2. Don't try to replace statements below with a React fragment —
            the routes don't get rendered and you will see a blank page.

            */}
          {isWhiteLabelLoginDomain() && (
            <Route path="/r/login">
              <WhiteLogin />
            </Route>
          )}
          {isWhiteLabelLoginDomain() && (
            <Route path="/r/forgot">
              <Forgot />
            </Route>
          )}
          {isWhiteLabelLoginDomain() && (
            <Route path="/r/change-password">
              <ChangePassword />
            </Route>
          )}
          {isWhiteLabelLoginDomain() && (
            <Route path="/confirm/:token">
              <IncompleteOfferConfirmationPage />
            </Route>
          )}

          <Route path="/r/2fa-setup">
            <TwoFactorAuthRequiredSetup />
          </Route>

          <Route path="/r/offers/accept/:offerNumber/:token">
            <OfferAccept />
          </Route>

          <Route path="/r/offer-drafts/accept/:offerDraftId/:token">
            <OfferDraftsAccept />
          </Route>

          <PrivateRoute path="/r">
            <JassBerryApp />
          </PrivateRoute>
        </Switch>
      )}
    </Suspense>
  )
}

const Root = ({ store }) => {
  useWebSocketsCleanup()
  const reactQueryDevtoolsActive =
    // @ts-expect-error
    __DEVELOPMENT__ &&
    new URL(document.location.toString()).searchParams.get(
      "react-query-devtools"
    )

  return (
    <Provider store={store} key="provider">
      <QueryClientProvider client={queryClient}>
        <HelmetProvider context={helmetContext}>
          <BrowserRouter>
            <Routes />
          </BrowserRouter>
        </HelmetProvider>
        {reactQueryDevtoolsActive && (
          <ReactQueryDevtools containerElement="div" />
        )}
        <ReactQuerySeleniumTools />
      </QueryClientProvider>
    </Provider>
  )
}

export default Root
